'use client';
import SearchIcon from '@/icons/search.svg';
import { GeneralSettings, WagtailNavigation } from '@/types';
import Hamburger from '@components/Hamburger';
import ContactModal from '@components/Header/ContactModal';
import { removeBaseUrl } from '@utils/url';
import clsx from 'clsx';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import MobileSubNavigation from './MobileSubNavigation';

type Props = {
  menu: WagtailNavigation[];
  settings?: GeneralSettings | null | undefined;
};

function MobileNavigation(props: Props) {
  const { menu, settings } = props;
  const [subMenuOpen, setSubMenuOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const hamburgerRef = useRef<HTMLButtonElement>(null);
  const pathname = usePathname();

  useEffect(() => {
    hamburgerRef.current?.setAttribute('aria-expanded', 'false');
  }, [pathname]);

  return (
    <div className='nav-bar__controls header__controls'>
      <div className={clsx('mobile-navigation', { open: subMenuOpen })}>
        <Hamburger ref={hamburgerRef} aria-label='Navigation umschalten' aria-controls='main-nav' />

        <nav className='nav' id='main-nav' aria-label='Hauptnavigation'>
          <ul role='list' className='nav__list'>
            {menu.map((item, index) => (
              <li className='nav__list-item' key={`${index}-${item.title}`}>
                {item.children.length ? (
                  <MobileSubNavigation
                    menuItem={item}
                    index={index}
                    activeIndex={activeIndex}
                    onClick={() => {
                      setSubMenuOpen(!subMenuOpen);
                      if (activeIndex === null || activeIndex !== index) {
                        setActiveIndex(index);
                      } else {
                        setActiveIndex(null);
                      }
                    }}
                  />
                ) : (
                  <Link href={removeBaseUrl(item.full_url)} className={clsx('nav__link')}>
                    {item.nav_title ? item.nav_title : item.title}
                  </Link>
                )}
              </li>
            ))}
          </ul>

          {settings && !subMenuOpen ? (
            <ul className='content-pages'>
              {settings.about_us_page ? (
                <li>
                  <Link className='content-pages__link' href={settings.about_us_page.full_url}>
                    Über uns
                  </Link>
                </li>
              ) : null}
              {settings.blog_index_page ? (
                <li>
                  <Link className='content-pages__link' href={settings.blog_index_page.full_url}>
                    Blog
                  </Link>
                </li>
              ) : null}
              {settings.conferences_page ? (
                <li>
                  <Link className='content-pages__link' href={settings.conferences_page.full_url}>
                    Konferenzen
                  </Link>
                </li>
              ) : null}
            </ul>
          ) : null}
          {settings ? (
            <ul className='legal legal__links'>
              {settings.imprint_page ? (
                <li>
                  <Link className='legal__link' href={settings.imprint_page.full_url}>
                    Impressum
                  </Link>
                </li>
              ) : null}
              {settings.terms_of_service_page ? (
                <li>
                  <Link className='legal__link' href={settings.terms_of_service_page.full_url}>
                    AGB
                  </Link>
                </li>
              ) : null}
              {settings.accessibility_page ? (
                <li>
                  <Link className='legal__link' href={settings.accessibility_page.full_url}>
                    Barrierefreiheit
                  </Link>
                </li>
              ) : null}
              {settings.data_protection_page ? (
                <li>
                  <Link className='legal__link' href={settings.data_protection_page.full_url}>
                    Datenschutz
                  </Link>
                </li>
              ) : null}
            </ul>
          ) : null}
        </nav>
      </div>
      <div className='nav-bar__buttons header__buttons'>
        {settings?.seminar_index_page ? (
          <Link
            href={removeBaseUrl(settings.seminar_index_page.full_url)}
            className='dialog-button menu-search'
            aria-label='Zur Suche'
          >
            <SearchIcon />
          </Link>
        ) : null}

        <ContactModal
          settings={settings}
          toggleOpen={() => hamburgerRef.current?.setAttribute('aria-expanded', 'false')}
          aria-label='Kontaktinformationen'
        />
      </div>
    </div>
  );
}

export default MobileNavigation;
